<template>
  <NuxtLayout name="catalog">
    <div class="page-ware">
      <div class="page-container">

        <!-- breadcrumb -->

        <MoleculesGlobalBreadcrumb :segments="(entity.groups?.find(group => group.main) ?? entity.groups?.sort((a, b) => b.group.id - a.group.id)[0])?.group?.parents"></MoleculesGlobalBreadcrumb>
        <div class="flex gap-8" ref="scrollTopElement">
          <div class="hidden tablet:block tablet:w-[350px] desktop:w-[40%] desktop:min-w-[450px]">

            <!-- main image - tablet & desktop -->

            <MoleculesGlobalWareImage :ware-name="entity.name" :image-id="mainImageId" :is-360="mainImageIs360" suffix="or" class="w-full aspect-square mx-auto p-8 cursor-pointer" @click="openLightBox(0)" />

            <!-- additional images -->

            <div v-if="shownAdditionalImages" class="page-ware__additional-images">
              <template v-for="(item, index) in shownAdditionalImages">
                <div class="flex-1 max-w-[20%] p-2 bg-white cursor-pointer">
                  <MoleculesGlobalWareImage :ware-name="item.name" :image-id="item.id" suffix="pd" @click="openLightBox(index + 1)" :is-360="item.type === 1 && !!item.parameters" />
                </div>
              </template>
              <button class="flex-1 aspect-square p-2 text-lg self-stretch bg-white cursor-pointer" v-show="additionalImages.length > 5" @click="openLightBox(6)">&plus; {{ additionalImages.length - 5 }}</button>
            </div>

            <!-- video -->

            <MoleculesBlocksVideoItem class="mt-2" v-if="entity.youtubeVideos.length" :title="entity.youtubeVideos[0].title" :youtube-id="entity.youtubeVideos[0].youtubeId" :thumbnails="entity.youtubeVideos[0].thumbnails" :is-our-channel="entity.youtubeVideos[0].isOurChannel" />

            <div v-if="entity.youtubeVideos.length > 1" class="text-right mt-2">
              <button @click="openSection(1, $event, false, true)"><span class="underline font-bold text-primary-500">{{ $t('Další videa') }}</span>
                <AtomsImagesIcon icon="arrow-right-carousel" size="xs" class="ml-2" />
              </button>
            </div>

          </div>
          <div class="flex-1">
            <div class="sticky top-8">

              <!-- badges -->

              <OrganismsBlocksBadges v-if="buyable" :ware-item="entity" :enable-links-on-text-badges="true" :sale-value-visible="true" :for-ware-detail="true" @badges-loaded="badgesLoaded" />

              <!-- name -->

              <h1 class="mt-4 mb-8 text-4xl font-bold tracking-normal">{{ entity.name }}</h1>

              <!-- images - mobile -->

              <div class="bg-white tablet:hidden">
                <Carousel :itemsToShow="1" :itemsToScroll="1" ref="mobileImages" class="mb-6">
                  <Slide :key="mainImageId">
                    <div>
                      <MoleculesGlobalWareImage :ware-name="entity.name" :image-id="mainImageId" suffix="or" class="max-h-[300px]" :is-360="mainImageIs360" @click="openLightBox(0)" />
                    </div>
                  </Slide>
                  <Slide v-for="(item, index) in additionalImages" :key="item.id">
                    <div>
                      <MoleculesGlobalWareImage :ware-name="item.name" :image-id="item.id" suffix="or" class="max-h-[300px]" :is-360="item.type === 1 && !!item.parameters" @click="openLightBox(index + 1)" />
                    </div>
                  </Slide>

                  <template #addons>
                    <Navigation />
                  </template>
                </Carousel>
              </div>

              <!-- variant selector -->

              <div v-if="buyable" class="page-ware__variant-selector">
                <OrganismsPanelsVariantSelector :variant-groups="entity.variantGroups" :variants="entity.variants" v-model="selectorModel" :main-image-id="entity.images?.[0]?.id" :ware-created="entity.created" :has-new-color-badge="hasNewColorBadge" :has-sizing-parameter="!!sizingDataRaw" @variant-group-names-generated="variantGroupsNamesGenerated" @parameters-to-be-selected-updated="(result) => parametersToBeSelected = result" @sizing-table-opened="openSizingTable" />
              </div>

              <!-- services #1 -->

              <div v-if="entity.services?.length" class="hidden items-center mt-6 p-4 tracking-normal bg-neutral-100 tablet:flex">
                <span class="mr-4 pr-4 text-xl font-bold border-r border-neutral-300 desktop:mx-8 desktop:pr-8">{{ $t('Další služby') }}</span>

                <template v-for="service in services">
                  <template v-if="service.options?.length">
                    <AtomsFormsCheckbox class="mr-4" v-model="service.checked">{{ service.name }} {{ $t('od') }} {{ locale.formatPrice(service.options.sort((a, b) => a.price - b.price)[0].price) }}</AtomsFormsCheckbox>
                    <AtomsTextLink :href="cache.getPageUrl(33262)" target="_blank" class="font-bold underline">{{ $t('Více informací') }}</AtomsTextLink>
                  </template>
                </template>
              </div>

              <!-- buy block -->

              <div class="flex flex-wrap gap-4 mt-8">

                <!-- conversion box -->

                <div class="flex flex-col gap-4 w-full desktop:flex-1">

                  <AtomsContainersCornerBorders v-if="!isPriceOnRequest" :size="1" class="flex-1 bg-neutral-100 px-[10%] py-12">
                    <div ref="scrollTopActionElement">

                      <template v-if="buyable">

                        <!-- price -->

                        <div class="flex flex-wrap items-center gap-x-8 gap-y-2">
                          <strong v-if="isVariantOnSale" class="w-full tablet:w-auto tablet:order-2 text-xl line-through text-neutral-300">{{ selectedVariant.prices.recommendedFormatted }}</strong>
                          <strong class="page-ware__price" v-if="Array.isArray(priceLabel)">
                            <span>{{ $t('od') }}</span> {{ priceLabel[0] }} <span>{{ $t('do') }}</span> {{ priceLabel[1] }}
                          </strong>
                          <strong class="page-ware__price" v-else>{{ priceLabel }}</strong>
                        </div>

                        <!-- availability -->

                        <div v-if="!isPersonalPickupOnly || isPreorder" class="flex flex-wrap gap-4 items-center my-4 min-h-[1.75rem] tracking-normal">
                          <template v-if="availabilityLabel != null">
                            <span v-if="!availabilityLabel" class="text-xl uppercase font-bold text-warning-500">{{ $t('Není skladem') }}</span>
                            <template v-else>

                              <span class="text-xl uppercase font-bold text-success-600">{{ availabilityLabel }}</span>

                              <div v-if="!shopZone.disabledFeatures.includes('DELIVERY_OPTIONS_IN_WARE')">
                                <span v-if="deliveryText" class="mr-2">{{ deliveryText }}</span>
                                <span v-if="isElectronicDistribution">{{ $t('Poukaz Vám pošleme e-mailem') }} (<button class="underline" @click="dialog.open('electronicDistributionInfo')">{{ $t('Kdy přesně?') }}</button>)</span>
                              </div>

                              <div v-if="hasLimitedShipping" class="text-error-600">
                                {{ $t('Product with limited shipping options outside EU') }} <button @click="openLimitedShippingDialog">(<span class="underline">{{ $t('find out more') }})</span></button>.
                              </div>

                            </template>
                          </template>

                          <template v-if="shippingPrice != null">

                            <!-- CZ + SK - button to open delivery options dialog -->

                            <button v-if="!shopZone.disabledFeatures.includes('DELIVERY_OPTIONS_IN_WARE')" @click="openDeliveryOptions">
                              <span class="inline-block w-[1.8rem] mr-1 text-left">
                                <AtomsImagesIcon icon="truck" />
                              </span>

                              <template v-if="shippingPrice === 0">
                                <span class="underline">{{ $t('Doprava zdarma') }}</span>
                              </template>

                              <template v-else>
                                <span class="underline">{{ $t('Doručení od {0}', [locale.formatPrice(shippingPrice)]) }}</span>
                              </template>
                            </button>

                            <!-- EN - link to infoPage -->

                            <AtomsTextLink v-else :to="cache.getPageUrl(26553)" target="_blank">
                              <span class="inline-block w-[1.8rem] mr-1 text-left">
                                <AtomsImagesIcon icon="truck" />
                              </span>

                              <template v-if="shippingPrice === 0">
                                <span class="underline">{{ $t('Doprava zdarma') }}</span>
                              </template>

                              <template v-else>
                                <span class="underline">{{ $t('Doručení od {0}', [locale.formatPrice(shippingPrice)]) }}</span>
                              </template>
                            </AtomsTextLink>

                          </template>
                        </div>

                        <!-- personal pickup only when not available in all shops -->

                        <div class="font-bold tracking-normal" v-if="isPersonalPickupOnly && !isPreorder">
                          <div class="mt-4 mb-4 text-lg text-neutral-500">{{ $t('Pouze osobní odběr na prodejnách') }}</div>

                          <div class="flex flex-wrap gap-4">

                            <AtomsContainersCornerBorders v-for="shop in storedOnShops" class="w-full p-4 text-center tablet:w-[150px]">
                              <div class="font-normal">{{ shop.name }}</div>
                              <div :class="{ 'text-success-600': shop.stored, 'text-neutral-500': !shop.stored }">
                                <template v-if="shop.stored">{{ $t('Skladem') }}</template>
                                <template v-else>{{ $t('Není skladem') }}</template>
                              </div>
                            </AtomsContainersCornerBorders>

                          </div>
                        </div>

                        <!-- watch dog -->

                        <div v-show="selectedVariant && watchDogOpened" class="max-w-[450px] mb-4">
                          <div class="text-lg mb-4 font-bold tracking-normal">{{ $t('Informujte mě o naskladnění') }}</div>
                          {{ $t('Zboží není momentálně skladem, chcete vědět jakmile ho doobjednáme a budeme mít skladem? Zadejte svůj e-mail a my vám dáme vědět.') }}
                          <MoleculesFormsValidatedField v-model="watchDogEmail" wrapper-class="bg-neutral-50" type="email" :placeholder="$t('Zadejte váš e-mail')" :error-message="$t('Vyplňte e-mail ve správném formátu')" :required="true" :ref="el => watchDogInput = el" />
                          <AtomsFormsCheckbox v-model="watchDogChecked" :label="$t('Souhlasím s obchodními podmínkami')" />
                          <MoleculesButtonsButton :arrow-right="true" class="mt-6 w-full" @click="addWatchDog">{{ $t('Ozvěte se mi') }}</MoleculesButtonsButton>
                        </div>

                        <div class="flex flex-wrap items-end gap-6 mt-6">

                          <!-- availability on stores -->

                          <button v-if="!shopZone.disabledFeatures.includes('WARE_AVAILABILITY_ON_STORES_DIALOG') && !isElectronicDistribution" class="inline-flex items-center gap-5 mt-2 font-bold text-lg text-left text-secondary-500" @click="openAvailabilityDialog">
                            <AtomsImagesIcon icon="store2" class="text-[2.25rem]" />
                            <div class="leading-5 tracking-normal">
                              <div class="text-[1.2rem] underline">{{ $t('Přehled dostupnosti') }}</div>
                              <span class="text-[0.9rem]">{{ $t('na prodejnách a e-shopu') }}</span>
                            </div>
                          </button>

                          <!-- card payment-->

                          <AtomsTextLink :href="cache.getPageUrl(26551)" target="_blank" class="inline-flex items-start gap-6 font-bold text-black hover:no-underline">
                            <AtomsImagesIcon icon="shield" class="ml-1.5 w-6.5 text-[2rem]" />
                            <div class="leading-5 tracking-normal">
                              <div class="text-[1.2rem] underline">{{ $t('Bezpečná platba kartou zdarma') }}</div>
                              <span class="text-[0.9rem]">{{ $t('a další možnosti platby') }}</span>
                            </div>
                          </AtomsTextLink>

                        </div>

                      </template>

                      <template v-else>
                        <div class="text-center tracking-normal">
                          <div class="my-8 text-3xl font-bold uppercase text-warning-500">{{ $t('Prodej produktu byl ukončen') }}</div>
                          <MoleculesButtonsButton v-if="entity.linked.length" @click="ui.scrollToElement(linkedWrapper)">{{ $t('Alternativní produkty') }}</MoleculesButtonsButton>
                        </div>
                      </template>
                    </div>
                  </AtomsContainersCornerBorders>

                  <!-- price on request -->

                  <AtomsContainersCornerBorders v-else :size="1" class="flex-1 bg-neutral-100 px-[10%] py-12">
                    <div class="uppercase text-2xl mb-2 font-bold tracking-normal">{{ $t('Cena na dotaz') }}</div>

                    <template v-if="availabilityLabel != null">
                      <span v-if="!availabilityLabel" class="text-xl uppercase font-bold text-warning-500">{{ $t('Není skladem') }}</span>
                      <template v-else>

                        <span class="text-xl uppercase font-bold text-success-600">{{ availabilityLabel.replace(i18n.t('Ihned k odeslání'), 'Skladem') }}</span>

                        <div v-if="hasLimitedShipping" class="text-error-600">
                          {{ $t('Product with limited shipping options outside EU') }} <button @click="openLimitedShippingDialog">(<span class="underline">{{ $t('find out more') }})</span></button>.
                        </div>

                      </template>

                      <div class="mt-2 text-lg" v-html="$t('Určeno pouze pro ozbrojené složky a IZS. Pro cenovou nabídku a objednávky kontaktujte <a class=underline href=mailto:ales.novotny(at)rigad.cz>ales.novotny(at)rigad.cz</a>')"></div>
                    </template>
                  </AtomsContainersCornerBorders>

                  <!-- add to cart -->

                  <div v-if="buyable && !isPriceOnRequest" v-show="!watchDogOpened" class="flex gap-2 tablet:gap-4">
                    <MoleculesFormsQuantityField v-show="availabilityLabel || isElectronicDistribution" v-model="quantity" :min="1" :flexible="true" class="tablet:w-32 tablet:text-lg" />
                    <MoleculesButtonsButton class="flex-1 px-0 py-2 tablet:py-3 tablet:text-lg" @click="actionClick">
                      <div class="flex justify-center items-center">
                        <AtomsImagesIcon :icon="availabilityLabel || !selectedVariant ? 'shopping-cart' : 'eye'" size="xl" class="mr-2" />
                        {{ ctaButtonText }}
                      </div>
                    </MoleculesButtonsButton>
                  </div>

                </div>

                <!-- services #2 -->

                <div v-if="entity.services?.length" class="flex-1 p-4 tracking-normal bg-neutral-100 tablet:hidden">
                  <div class="mb-4 text-xl font-bold">{{ $t('Další služby') }}</div>

                  <template v-for="service in services">
                    <template v-if="service.options?.length">
                      <AtomsFormsCheckbox v-model="service.checked"><span class="mr-2">{{ service.name }} {{ $t('od') }} {{ locale.formatPrice(service.options.sort((a, b) => a.price - b.price)[0].price) }}</span>
                        <AtomsTextLink :href="cache.getPageUrl(33262)" target="_blank" class="font-bold underline">{{ $t('Více informací') }}</AtomsTextLink>
                      </AtomsFormsCheckbox>
                    </template>
                  </template>
                </div>

                <!-- side panel -->

                <div class="w-full flex flex-col gap-4 tablet:order-[-1] tablet:flex-row desktop:order-[1] desktop:flex-col desktop:w-[280px]">

                  <!-- guide -->

                  <div v-if="guideInfoPage" class="flex-1 flex justify-center items-center p-6 bg-neutral-100">
                    <div class="text-center">
                      <AtomsImagesIcon icon="question" size="2xl" />
                      <div>{{ $t('Nevíte si rady?') }}</div>
                      <AtomsTextLink :to="guideInfoPage.url" class="font-bold underline text-primary-500">{{ guideInfoPage.title }}</AtomsTextLink>
                    </div>
                  </div>

                  <div class="flex-1 flex flex-wrap flex-col gap-4 tablet:min-w-[300px] tablet:flex-row desktop:min-w-[auto] desktop:flex-col">

                    <!-- vendor -->

                    <AtomsTextLink v-if="vendorData" :href="vendorData.url" class="flex-1 flex items-center justify-center p-4 border border-neutral-200">
                      <AtomsImagesImage :src="`${useShopZone().value.staticResourcesRoot}${vendorData.imagePath}`" :alt="vendorData.name" :title="vendorData.name" :class="{ 'max-w-20': vendorData.armyOriginal }" />
                    </AtomsTextLink>

                    <!-- codes -->

                    <div class="flex-1 flex flex-col items-center justify-center p-2 leading-7 text-center border border-neutral-200 min-h-[7rem]">
                      <div v-if="defaultVariantCode">{{ $t('Kód produktu') }}: {{ selectedVariant?.catalogCode?.[0]?.code ?? defaultVariantCode }}</div>
                      <div v-show="selectedVariant && selectedVariant.eanCode?.length === 1">{{ $t('EAN') }}: {{ selectedVariant?.eanCode[0]?.code }}</div>
                      <div>{{ $t('Délka záruky') }}: {{ getParameterValue(appConfig.parameterIds.warranty) ?? i18n.t('24 měsíců') }}</div>
                    </div>

                  </div>
                </div>

              </div>
            </div>
          </div>
        </div>

        <!-- sections block-->

        <div class="page-ware__section__wrapper" ref="sectionsWrapper">
          <div class="page-ware__section__menu">
            <div class="sticky top-16 -mb-[1px]">
              <strong v-if="entity.description?.trim().length" class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 0 }" @click="openSection(0, $event, false, true)">
                {{ $t('Popis a parametry') }}
              </strong>
              <strong v-if="entity.youtubeVideos.length" class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 1 }" @click="openSection(1, $event, false, true)">
                {{ $t('Video') }}
              </strong>
              <strong v-if="entity.attachments.length" class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 2 }" @click="openSection(2, $event, false, true)">
                {{ $t('Návody a soubory ke stažení') }}
              </strong>
              <strong v-if="articles?.length" class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 3 }" @click="openSection(3, $event, false, true)">
                {{ $t('Související články') }}
              </strong>
              <strong v-if="false" class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 4 }" @click="openSection(4, $event, false, true)">
                {{ $t('Recenze') }}
              </strong>
              <strong class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 5 }" @click="openSection(5, $event, false, true)">
                {{ $t('Dotaz k produktu') }}
              </strong>
            </div>
          </div>
          <div class="flex-1 min-w-0 tablet:flex flex-wrap desktop:block">

            <!-- description -->

            <strong class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 0 }" @click="openSection(0, $event)">
              {{ $t('Popis a parametry') }}
              <AtomsImagesIcon icon="arrow-right-carousel" size="lg" />
            </strong>

            <section class="page-ware__section" :class="{ 'page-ware__section--open': openSectionIndex === 0 }">
              <div class="page-ware__section__inner">
                <div class="flex flex-wrap gap-x-8">
                  <div class="w-full desktop:flex-1">
                    <div class="page-ware__description" v-html="entity.description"></div>
                    <!-- sub description -->

                    <div v-if="entity.subDescription" class="page-ware__description" v-html="processedSubDescription"></div>

                    <!-- action button -->

                    <OrganismsPanelsWareActionButton v-if="buyable && !isPriceOnRequest" :ware-name="entity.name" :price-text="ctaPriceText" :price-formatted="ctaPriceFormatted" :button-text="ctaButtonText" @click="actionClick" class="!my-8" />

                  </div>
                  <div v-if="descriptionProperties?.length" class="w-full desktop:w-[430px]">

                    <!-- properties -->

                    <div class="page-ware__sub-header !mt-4">{{ $t('Důležité parametry') }}</div>
                    <table class="page-ware__properties">
                      <tbody>
                        <tr v-for="property in descriptionProperties">
                          <td>{{ property.name }}</td>
                          <td>
                            <div v-for="value in property.values" v-html="value.valueTagged">
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </section>

            <!-- videos -->

            <template v-if="entity.youtubeVideos.length">
              <strong class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 1 }" @click="openSection(1, $event)">
                {{ $t('Video') }}
                <AtomsImagesIcon icon="arrow-right-carousel" size="lg" />
              </strong>

              <section class="page-ware__section" :class="{ 'page-ware__section--open': openSectionIndex === 1 }">
                <div class="page-ware__section__inner">
                  <strong class="page-ware__section__label">{{ $t('Video') }}</strong>
                  <div class="space-y-4">
                    <MoleculesBlocksVideoItem v-for="video in entity.youtubeVideos" :title="video.title" :youtube-id="video.youtubeId" :thumbnails="video.thumbnails" :is-our-channel="video.isOurChannel" />
                  </div>

                  <!-- action button -->

                  <OrganismsPanelsWareActionButton v-if="buyable" :ware-name="entity.name" :price-text="ctaPriceText" :price-formatted="ctaPriceFormatted" :button-text="ctaButtonText" @click="actionClick" />
                </div>
              </section>
            </template>

            <!-- attachments / files -->

            <template v-if="entity.attachments.length">
              <strong class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 2 }" @click="openSection(2, $event)">
                {{ $t('Návody a soubory ke stažení') }}
                <AtomsImagesIcon icon="arrow-right-carousel" size="lg" />
              </strong>

              <section class="page-ware__section" :class="{ 'page-ware__section--open': openSectionIndex === 2 }">
                <div class="page-ware__section__inner">
                  <strong class="page-ware__section__label">{{ $t('Návody a soubory ke stažení') }}</strong>

                  <AtomsTextLink v-for="attachment in entity.attachments" :href="attachment.url" target="_blank" class="flex items-center gap-4 hover:no-underline">
                    <AtomsImagesIcon icon="described-paper" size="3xl" class="block mb-1" /> {{ attachment.type.name }}
                  </AtomsTextLink>

                </div>
              </section>
            </template>

            <!-- related articles -->

            <template v-if="articles?.length">
              <strong class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 3 }" @click="openSection(3, $event)">
                {{ $t('Související články') }}
                <AtomsImagesIcon icon="arrow-right-carousel" size="lg" />
              </strong>
              <section class="page-ware__section" :class="{ 'page-ware__section--open': openSectionIndex === 3 }">
                <div class="page-ware__section__inner">
                  <strong class="page-ware__section__label">{{ $t('Související články') }}</strong>

                  <div v-for="infoPage in articles" class="flex flex-wrap items-center mb-2 tracking-normal bg-neutral-100">
                    <AtomsTextLink :to="infoPage.page.url" class="w-full tablet:w-auto">
                      <AtomsImagesImage :src="`${useShopZone().value.staticResourcesRoot}/img/magazine/${infoPage.id}.jpg`" :alt="infoPage.name" :title="infoPage.name" class="w-full tablet:max-w-[220px]" />
                    </AtomsTextLink>
                    <div class="flex-1 p-6 desktop:p-8">
                      <strong class="block mb-2">{{ infoPage.page.title }}</strong>
                      <AtomsTextLink :to="infoPage.page.url" class="font-bold text-sm underline uppercase text-primary-500">{{ $t('Přečíst článek') }}</AtomsTextLink>
                    </div>
                  </div>

                  <!-- action button -->

                  <OrganismsPanelsWareActionButton v-if="buyable" :ware-name="entity.name" :price-text="ctaPriceText" :price-formatted="ctaPriceFormatted" :button-text="ctaButtonText" @click="actionClick" />
                </div>
              </section>
            </template>

            <!-- reviews -->

            <template v-if="false">
              <strong class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 4 }" @click="openSection(4, $event)">
                {{ $t('Recenze') }}
                <AtomsImagesIcon icon="arrow-right-carousel" size="lg" />
              </strong>

              <section class="page-ware__section" :class="{ 'page-ware__section--open': openSectionIndex === 4 }">
                <div class="page-ware__section__inner">
                  <strong class="page-ware__section__label">{{ $t('Recenze') }}</strong>

                  <!-- action button -->

                  <OrganismsPanelsWareActionButton v-if="buyable" :ware-name="entity.name" :price-text="ctaPriceText" :price-formatted="ctaPriceFormatted" :button-text="ctaButtonText" @click="actionClick" />
                </div>
              </section>
            </template>

            <!-- inquiry -->

            <h2 class="page-ware__section__header" :class="{ 'page-ware__section__header--open': openSectionIndex === 5 }" @click="openSection(5, $event)">
              {{ $t('Dotaz k produktu') }}
              <AtomsImagesIcon icon="arrow-right-carousel" size="lg" />
            </h2>
            <section class="page-ware__section" :class="{ 'page-ware__section--open': openSectionIndex === 5 }">
              <div class="page-ware__section__inner">
                <strong class="page-ware__section__label">{{ $t('Dotaz k produktu') }}</strong>
                <OrganismsPanelsWareInquiryForm />

                <!-- action button -->

                <OrganismsPanelsWareActionButton v-if="buyable" :ware-name="entity.name" :price-text="ctaPriceText" :price-formatted="ctaPriceFormatted" :button-text="ctaButtonText" @click="actionClick" />
              </div>
            </section>

          </div>
        </div>

        <!-- related products -->

        <div v-if="entity.linked.length" ref="linkedWrapper">
          <AtomsTextHeading class="mt-8">{{ $t('Související produkty') }}</AtomsTextHeading>
          <OrganismsListsWareList :items="entity.linked.map(item => item.ware)" type="slider" :with-slider-navigation="true" list-name="Související produkty" class="mb-4 desktop:-mt-4 desktop:-mb-8" item-name-element-override="div"></OrganismsListsWareList>
        </div>

        <!-- categories -->

        <h2 class="mt-8 mb-4 font-bold uppercase tracking-normal text-primary-500">{{ $t('Kategorie produktu') }}</h2>
        <ul class="page-ware__labels mb-8">
          <li v-for="group in entity.groups.flatMap(jointGroup => jointGroup.group.parents.find(parentGroup => parentGroup.level === 0))">
            <AtomsTextLink :href="group.url">{{ group.name }}</AtomsTextLink>
          </li>
        </ul>

        <!-- variants -->

        <div v-if="variantGroupLinks.length" class="mb-8 p-4 border border-neutral-100 tablet:p-0 tablet:border-0">
          <h2 class="flex justify-between font-bold uppercase tracking-normal text-primary-500 cursor-pointer tablet:cursor-default tablet:mt-8 tablet:mb-4" @click="variantGroupsExpanded = !variantGroupsExpanded">
            {{ $t('Varianty') }}
            <AtomsImagesIcon icon="arrow-right-carousel" size="md" class="mt-1 w-4 h-4 transition-all rotate-90 tablet:hidden" :class="{ '!-rotate-90': variantGroupsExpanded }" />
          </h2>
          <MoleculesContainersSlideToggle :expanded="variantGroupsExpanded" class="tablet:!max-h-full tablet:!opacity-100">
            <ul class="page-ware__labels mt-4 tablet:mt-0">
              <li v-for="variantGroupLink in variantGroupLinks">
                <h3>
                  <AtomsTextLink :to='`#${variantGroupLink.defaultVariantId}`' @click="ui.scrollToElement(scrollTopElement)">{{ entity.name }} - {{ variantGroupLink.name }}</AtomsTextLink>
                </h3>
              </li>
            </ul>
          </MoleculesContainersSlideToggle>
        </div>

        <!-- alternative links -->

        <div v-if="alternativeLinks.length" class="flex flex-wrap gap-x-6 gap-y-4 mb-12">
          <div v-for="alternativeLink in alternativeLinks" class="page-ware__alternative-link">
            <img class="inline-block w-6 mr-4 mt-[4px] rounded-[4px] tablet:-mt-[6px]" :src="`/img/flags/${(alternativeLink.locale === 'en' ? 'eu' : alternativeLink.locale)}.svg`" :alt="alternativeLink.locale" />

            <span>

              <template v-if="alternativeLink.locale === 'en'">
                {{ $t('Worldwide delivery? Go to ') }}
              </template>

              <template v-else-if="alternativeLink.locale === 'cs'">
                {{ $t('Doručení do České republiky? Přejděte na ') }}
              </template>

              <template v-else-if="alternativeLink.locale === 'sk'">
                {{ $t('Doručenie na Slovensko? Prejdite na ') }}
              </template>

              <AtomsTextLink :href="alternativeLink.absoluteUrl" target="_blank" class="font-bold  text-primary-500 underline" :hreflang="alternativeLink.locale">{{ alternativeLink.name }}</AtomsTextLink>
            </span>
          </div>
        </div>

        <!-- recently viewed items -->

        <LazyTemplatesRecentlyViewedItems />

        <!-- usp -->

        <OrganismsBlocksUsps class="mb-4 desktop:mb-6" item-class="!bg-neutral-100 desktop:aspect-[7/3]" />

      </div>
    </div>

    <!-- sticky header (variable conversion bar and message bar) -->

    <div v-if="!isPriceOnRequest" class="fixed top-0 w-full z-[2002]" data-pagearea="product_conversion_top_bar">
      <Transition name="slidein">
        <div v-if="buyable && conversionBarOpened" class="bg-neutral-500 text-neutral-50">
          <div class="page-container">
            <div class="flex justify-end items-center gap-4 py-[5px]">
              <div class="hidden tablet:block">
                <MoleculesGlobalWareImage :ware-name="entity.name" :image-id="mainImageId" suffix="or" class="w-[50px] p-1" />
              </div>

              <div class="text-sm tablet:text-base">
                {{ entity.name }}
                <div class="font-bold">

                  <template v-if="Array.isArray(priceLabel)">
                    <span>{{ $t('od') }}</span> {{ priceLabel[0] }} <span>{{ $t('do') }}</span> {{ priceLabel[1] }}
                  </template>

                  <template v-else>{{ priceLabel }}</template>
                </div>
              </div>

              <MoleculesButtonsButton class="py-2 text-sm tablet:py-3 tablet:ml-4 tablet:text-base" @click="actionClick(true)">{{ selectedVariant ? ctaButtonText : $t('Vyberte variantu') }}</MoleculesButtonsButton>
            </div>
          </div>
        </div>
      </Transition>
      <OrganismsDialogsMessage :show="currentMessage.show" :icon="currentMessage.icon" :text="currentMessage.text" :button-text="currentMessage.buttonText" :type="currentMessage.type" @hide="message.hide()" />
    </div>

    <VueEasyLightbox :visible="lightBoxOpened" :index="lightBoxIndexRef" :imgs="lightBoxImages" :min-zoom="0.12" @hide="closeLightBox" />
    <LazyOrganismsPanelsSizingTable />
    <LazyOrganismsDialogsAddedToCart />
    <LazyOrganismsDialogsCartOvercount />
    <LazyOrganismsDialogsAvailabilityOnStores />
    <LazyOrganismsDialogsSelectParameters />
    <LazyOrganismsDialogsDeliveryOptions />
    <LazyOrganismsDialogsEngraving @close="({ cartItem, withEngraving }) => dialog.open('addedToCart', { cartItem: cartItem, includeLastService: withEngraving })" />
    <LazyOrganismsDialogsElectronicOrder v-if="isElectronicDistribution" />
    <LazyOrganismsDialogsElectronicDistributionInfo />
    <LazyOrganismsDialogsLimitedShipping />
  </NuxtLayout>
</template>

<script setup>

useState('serverTiming', () => []).value.push({ started: new Date().getTime(), description: 'Ware Page Load Started' });

import VueEasyLightbox from '~/components/molecules/global/lightbox/index.ts'
import "assets/css/lightbox.css";
import { Carousel, Slide, Navigation, Pagination } from '~/components/molecules/containers/carousel.js';
import { useBreakpoints, useElementBounding } from '@vueuse/core';
import tippy from 'tippy.js'
import "assets/css/tippy.css";

const entity = useEntity();

/* simulation of ware without images
entity.variantGroups.forEach(item => item.wareImage = null);
entity.images = [];
entity.image = null;
*/


/* simulation of ware with preorder 
entity.tags = [{ type: 'PREORDER', value: 1729062068, __typename: 'WareTag' }];

entity.variants.forEach(variant => {
  variant.preorder = '2024-10-16T00:00:00.000+02:00';
});
*/

const router = useRouter();
const utils = useUtils();
const appConfig = useAppConfig();
const locale = useLocale();
const ui = useUI();
const shopZone = useShopZone();
const i18n = useI18n();
const view = useBreakpoints(appConfig.breakpoints);
const availability = useAvailability();
const business = useBusiness();
const dialog = useDialog();
const cache = useCache();
const message = useMessage();
const analytics = useBaseAnalytics();
const wareAnalytics = useWareAnalytics();

const selectorModel = ref({
  selectedVariant: null,
  selectedVariantGroup: null,
});

const selectedVariant = computed(() => selectorModel.value.selectedVariant);
const selectedVariantGroup = computed(() => selectorModel.value.selectedVariantGroup);

const parametersToBeSelected = ref(null);
const hasNewColorBadge = ref(false);
const quantity = ref(1);
const scrollTopElement = ref(null);
const scrollTopActionElement = ref(null);
const shippings = ref([]);
const linkedWrapper = ref(null);
const variantGroupsExpanded = ref(false);

const currentMessage = message.message;

const services = reactive(entity.services || []);

// if there are more variants, filter out the ones that have no properties
if (entity.variants.length > 1) {
  entity.variants = entity.variants.filter(variant => variant.properties.length > 0);
}

const buyable = entity.variants.length && entity.variants.some(variant => !variant.clearanceSale || variant.stored) && entity.variants.every(variant => variant.prices.price > 0);

entity.properties = entity.properties.concat(entity.customProperties).concat(entity.customProperties1);

const badgesLoaded = (badges) => {
  if (badges?.some(badge => badge.type === 'NEW_COLOR')) {
    hasNewColorBadge.value = true;
  }
}

const getVariantsForGroup = (variantGroup) => {
  return entity.variants.filter(variant => variant.variantGroups.some(vg => vg.id === variantGroup?.id));
}

const isPersonalPickupOnly = business.isWarePersonalPickupOnly(entity.properties);
const isElectronicDistribution = business.isWareElectronicDistribution(entity);
const isOverride30days = business.isWareOverride30days(entity);
const isPreorder = business.isPreorder(entity);
const isPriceOnRequest = business.isWarePriceOnRequest(entity);

const allShopsStored = computed(() => {

  if (!selectedVariant.value) {
    return true;
  }

  return storedOnShops.value.every(shop => shop.stored);
});

const storedOnShops = computed(() => {

  if (!selectedVariant.value) {
    return null;
  }

  return appConfig.suppliers.filter(supplier => supplier.isShop).map(shop => {
    return {
      idSupplier: shop.supplierId,
      name: shop.name,
      stored: selectedVariant.value.stores.some(store => store.idSupplier === shop.supplierId && store.stored)
    }
  });
});

const priceLabel = computed(() => {

  let result = entity.price.minPrice.priceFormatted;

  // show price of selected variant
  if (selectedVariant.value) {
    result = selectedVariant.value.prices.priceFormatted;
  }

  // show price of selected variant group
  else if (selectedVariantGroup.value) {

    const variantsOfSelectedVariantGroup = getVariantsForGroup(selectedVariantGroup.value);

    const minPriceWithinVariantGroup = variantsOfSelectedVariantGroup.sort((a, b) => a.prices.price - b.prices.price)[0].prices.priceFormatted;
    const maxPriceWithinVariantGroup = variantsOfSelectedVariantGroup.sort((a, b) => b.prices.price - a.prices.price)[0].prices.priceFormatted;

    // show price range if there are variants with different prices within the variant group
    if (minPriceWithinVariantGroup != maxPriceWithinVariantGroup) {
      result = [minPriceWithinVariantGroup, maxPriceWithinVariantGroup];
    }
    else {
      result = variantsOfSelectedVariantGroup[0].prices.priceFormatted;
    }
  }

  // show price range if there are variants with different prices
  else if (entity.price.minPrice.price != entity.price.maxPrice.price) {
    result = [entity.price.minPrice.priceFormatted, entity.price.maxPrice.priceFormatted];
  }

  return result;
});

const availabilityLabel = computed(() => {

  const v = selectedVariant.value;

  if (!v) {
    return null;
  }

  let result = availability.getVariantLabel(
    isElectronicDistribution,
    isPersonalPickupOnly,
    v.stored,
    v.storedOnOwnStore,
    v.preorder,
    isOverride30days,
    i18n);

  return result;
});

const shippingPrice = computed(() => {

  const v = selectedVariant.value;

  let result;

  if (!entity.shippingOptions?.cheapest) {
    result = null;
  }
  else if (!v) {
    result = entity.shippingOptions.cheapest.price;
  }
  else {
    result = v.cheapestShippingLevel.price;
  }

  return result;
});

const dispatchDate = computed(() => {

  let result;

  if (selectedVariant.value) {
    const defaultSupplierId = appConfig.suppliers.find(supplier => supplier.isDefault)?.supplierId;
    const sourceSupplierId = selectedVariant.value.stores.filter(store => store.stored).sort((a, b) => a.idSupplier === defaultSupplierId ? -1 : 1)[0]?.idSupplier;

    result = availability.getVariantDispatchDate(selectedVariant.value.stored, selectedVariant.value.storedOnOwnStore, selectedVariant.value.deliveryPeriod, isOverride30days, selectedVariant.value.preorder, sourceSupplierId, defaultSupplierId);
  }

  return result;
});

const deliveryText = computed(() => {

  let result = null;

  const fastestDeliveryDays = entity.shippingOptions?.fastest?.estimatedDeliveryDays;

  if (typeof fastestDeliveryDays === 'number') {
    const deliveryDate = availability.getVariantDeliveryDate(dispatchDate.value, fastestDeliveryDays);

    result = availability.getVariantDeliveryText(deliveryDate, false, i18n);
  }
  else if (isPreorder) {
    result = !isNaN(entity.tags.find(tag => tag.type === 'PREORDER')?.value) ? utils.formatDate(new Date(parseInt(entity.tags.find(tag => tag.type === 'PREORDER').value))) : '';
  }

  return result;
});

const getParameterValue = (parameterId) => entity.properties.find(property => property.id === parameterId)?.values?.[0]?.value;
const getParameterLvalue = (parameterId) => entity.properties.find(property => property.id === parameterId)?.values?.[0]?.lValue;

const defaultVariantCode = entity.variants.sort((a, b) => a.prices.price - b.prices.price)[0]?.catalogCode?.[0]?.code;

//#region images

const getPriorityOfImage = (image) => entity.image?.id === image.id ? -10000 + image.priority : (entity.variantGroups.some(variantGroup => variantGroup.wareImage?.id === image.id) ? -1000 + image.priority : image.priority);

const sortedImages = entity.images?.sort((a, b) => getPriorityOfImage(a) - getPriorityOfImage(b));

const filteredImages = computed(() => {
  let result;

  if (selectedVariantGroup.value) {
    result = sortedImages?.filter(image => image.idsVariantsGroups?.length === 0 || image.idsVariantsGroups.includes(selectedVariantGroup.value?.id));

    if (result.length === 0) {
      result = [sortedImages?.[0]];
    }
  } else {
    result = sortedImages;
  }

  return result;
});

const mainImageId = computed(() => filteredImages.value?.[0]?.id);
const mainImageIs360 = computed(() => filteredImages.value?.[0]?.type === 1 && filteredImages.value?.[0]?.parameters);
const additionalImages = computed(() => filteredImages.value?.filter(image => image?.id !== mainImageId.value));
const shownAdditionalImages = computed(() => additionalImages.value?.slice(0, 5));

const lightBoxOpened = ref(false);
const lightBoxIndexRef = ref(0);
const lightBoxImages = computed(() => filteredImages.value?.map((item) => {
  return {
    src: utils.getWareImageUrl(item?.id, entity.name, "or"),
    iframeSrc: item?.parameters
  }
}));

const mobileImages = ref(null);

const openLightBox = (index) => {
  lightBoxIndexRef.value = index;
  lightBoxOpened.value = true;
}

const closeLightBox = () => lightBoxOpened.value = false;

//#endregion

//#region variant groups

const variantGroupLinks = ref([]);

const variantGroupsNamesGenerated = (variantGroupsNames) => {
  variantGroupLinks.value = variantGroupsNames;
}

//#endregion

//#region alternative links

const alternativeLinks = usePage().value.page.alternativeUrls.filter(alternativeUrl => shopZone.value.relatedShopZoneIds.includes(alternativeUrl.shopZone.id)).map(alternativeUrl => {
  return {
    absoluteUrl: alternativeUrl.absoluteUrl,
    locale: appConfig.shopZones.find(shopZone => alternativeUrl.shopZone.id === shopZone.shopZoneId).locale,
    name: entity.otherLanguageNames.find(otherLanguageName => otherLanguageName.language.id === alternativeUrl.language.id)?.name
  }
});

//#endregion

//#region sizing table

const sizingDataRaw = entity.properties.find(property => property.id === appConfig.parameterIds.sizing)?.values?.[0]?.description;
let sizingData;

if (sizingDataRaw && sizingDataRaw.startsWith('<!--') && sizingDataRaw.endsWith('-->')) {
  const jsonStr = sizingDataRaw.substring('<!--'.length, sizingDataRaw.length - '-->'.length).replaceAll('\n', '').replaceAll('\t', '');

  sizingData = JSON.parse(jsonStr);
}

const openSizingTable = () => {
  useDialog().open('sizingTable', { sizingData: sizingData });
}

//#endregion

//#region recently viewed items

// append to recently viewed items with delay so this pageload will not push this item directly to the beginning of the list
if (process.client) {
  setTimeout(() => useRecentlyViewedItems().addItem(entity.id), 1000);
}

//#endregion

//#region sections

const sectionsWrapper = ref(null);
const openSectionIndex = ref(0);

const openSection = (index, event, scroll, scrollToWrapper) => {

  if (openSectionIndex.value === index && view.smallerOrEqual('tablet').value) {
    openSectionIndex.value = -1;
  }
  else {
    openSectionIndex.value = index;

    if (scrollToWrapper) {
      setTimeout(() => ui.scrollToElement(sectionsWrapper.value, 0), 350);
    }
    else if (scroll || scroll === undefined) {
      setTimeout(() => ui.scrollToElement(event.target, 0), 350);
    }
  }
}

//#endregion

const processedSubDescription = computed(() => {
  return entity.subDescription?.replace(/<table/g, '<div class="table-wrapper"><table').replace(/<\/table>/g, '</table></div>');
});

//#region description parameters

const disabledParameterIdsForTable = [
  appConfig.parameterIds.sizing,
  appConfig.parameterIds.vendor,
];

const descriptionProperties = entity.properties.filter(property => {

  let result =
    property.showOnDetail
    && !disabledParameterIdsForTable.includes(property.id)
    && (property.id !== appConfig.parameterIds.material || !entity.properties.some(p => p.id === appConfig.parameterIds.materialDetailed))
    && (property.id !== appConfig.parameterIds.weight || !entity.properties.some(p => p.id === appConfig.parameterIds.weightDetailed))
    && (property.type !== 'NUMBER' || property.values?.length === 1)
    && (!appConfig.parameterIdsToHideWithMultipleValues.includes(property.id) || property.values?.length === 1)

  return result;
}).sort((a, b) => b.priority - a.priority).map(property => {
  return {
    name: property.name.indexOf(':') > -1 ? property.name.substring(property.name.indexOf(':') + 1).trim() : property.name,
    values: property.values.sort((a, b) => b.priority - a.priority)
  }
});

//#endregion

//#region action buttons

const ctaButtonText = computed(() => {

  let result;

  if (isElectronicDistribution) {
    result = i18n.t('Zakoupit');
  }
  else if (!selectedVariant.value || availabilityLabel.value) {
    result = i18n.t('Přidat do košíku');
  }
  else {
    result = i18n.t('Hlídat dostupnost');
  }

  return result;

});

const ctaPriceText = computed(() => {
  return (Array.isArray(priceLabel.value) ? i18n.t('od') : (isVariantOnSale ? i18n.t('za akční cenu') : i18n.t('za')));
});


const ctaPriceFormatted = computed(() => {
  return (Array.isArray(priceLabel.value) ? priceLabel.value[0] : priceLabel.value);
});

const isVariantOnSale = computed(() => selectedVariant.value?.prices.price < selectedVariant.value?.prices.recommended);

const actionClick = async (suppressParametersDialog) => {
  if (selectedVariant.value) {
    if (isElectronicDistribution) {
      dialog.open('electronicOrder', {
        wareImageId: mainImageId.value,
        wareName: entity.name,
        variantId: selectedVariant.value.id,
        variantName: selectedVariant.value.variantName,
        piecePrice: selectedVariant.value.prices.price,
        unit: entity.unit,
        quantity: quantity.value,
      });
    }
    else if (availabilityLabel.value) {

      const cartItemData = await useCart().add(selectedVariant.value.id, quantity.value);

      if (cartItemData.addedQuantity) {

        let selectedService = services.find(service => service.checked);

        if (selectedService?.id === appConfig.wareServiceIds.engraving) {

          useWareAnalytics().getItems(
            [entity.id]
          ).then((data) => {

            if (data?.length) {

              dialog.open('engraving', {
                wareName: entity.name,
                wareImageId: mainImageId,
                wareCategory: data?.[0]?.item_category,
                serviceId: selectedService.id,
                serviceName: selectedService.name,
                serviceOptions: selectedService.options,
                cartItemData: cartItemData.cartItem,
                source: 'Detail produktu'
              });

            }
          });
        }
        else {
          dialog.open('addedToCart', cartItemData);
        }
      }
    }
    else {
      if (!watchDogOpened.value) {
        watchDogOpened.value = true;
      }
      ui.scrollToElement(scrollTopActionElement.value, 60);
    }
  }
  else if (parametersToBeSelected.value && parametersToBeSelected.value.length) {

    if (!suppressParametersDialog) {
      useDialog().open('selectParameters', { parameters: parametersToBeSelected.value });
    }

    ui.scrollToElement(scrollTopElement.value, 60);
  }

}

//#endregion

//#region vendor

const vendorData = useState('vendorData-ware:' + entity.id, () => null);

if (!vendorData.value) {

  const vendorLValue = getParameterLvalue(appConfig.parameterIds.vendor);
  const countryArmyValue = getParameterValue(appConfig.parameterIds.countryArmy);
  const countryArmyLValue = getParameterLvalue(appConfig.parameterIds.countryArmy);

  // vendor specified

  if (vendorLValue) {
    useApiFetch(`/api/vendorByLvalue?lValue=${vendorLValue}&currencyId=${locale.getLocale().currencyId}&languageId=${locale.getLocale().languageId}`).then(data => {
      vendorData.value = data.custom.ta.vendor;

      if (vendorData.value?.idImage) {
        vendorData.value.imagePath = `/img/groups/vendors1/${vendorData.value.idImage}.png`;
      }
    });
  }

  // no vendor but army original parameter value is present and country of army is specified

  else if (countryArmyValue) {

    let root = useShopZone().value.staticResourcesRoot;

    if (process.env.NODE_ENV === 'development') {
      root = 'http://localhost:3000';
    }

    const imagePath = `${root}/img/originalFlags/${countryArmyLValue}.png`;
    const imageResponse = await fetch(imagePath);

    if (imageResponse.status === 200) {

      const parameterUrlName = utils.slugify(entity.properties.find(property => property.id === appConfig.parameterIds.countryArmy).name);
      const parameterUrlValue = utils.slugify(countryArmyValue);

      vendorData.value = {
        url: `${cache.getPageUrl(18399)}#!${parameterUrlName}=${parameterUrlValue}`,
        name: countryArmyValue,
        lValue: countryArmyLValue,
        imagePath: `/img/originalFlags/${countryArmyLValue}.png`,
        armyOriginal: true
      }
    }
  }
}

//#endregion

//#region articles & guide

const articles = entity.infoPages?.infoPages?.items.filter(infoPage => infoPage.magazineCategory?.id !== appConfig.magazineCategories.reviews);
const guideInfoPage = entity.guideInfoPage?.infoPages?.items?.[0]?.page;

//#endregion

//#region availability dialog

const openAvailabilityDialog = () => {
  dialog.open('availabilityOnStores', {
    variants: entity.variants.map(variant => {
      return {
        name: variant.variantName,
        stores: variant.stores,
        onWay: variant.stored && !variant.storedOnOwnStore,
      };
    })
  });
}

//#endregion

//#region delivery options / limited shipping

const openDeliveryOptions = async () => {

  // When there is preferred country for current shopzone, load available shippings
  if (!shippings.value.length && entity.variants.length && shopZone.value.countryIds?.[0]) {
    shippings.value = (await useApiFetch(`/api/shippingLevels?countryId=${shopZone.value.countryIds?.[0]}&currencyId=${locale.getLocale().currencyId}&languageId=${locale.getLocale().languageId}`, {
      method: 'POST',
      body: {
        items: [
          {
            idVariant: entity.variants[0].id,
            count: 1,
          }
        ]
      }
    })).shippingLevels.sort((a, b) => a.priority - b.priority);
  }

  const specificWareDataForDeliveryComputation = {
    id: '0',
    idVariant: selectedVariant.value.id,
    isVirtual: false,
    count: 1,
    stored: selectedVariant.value.stored,
    storedOnOwnStore: selectedVariant.value.storedOnOwnStore,
    deliveryPeriod: selectedVariant.value.deliveryPeriod,
    preorder: selectedVariant.value.preorder,
    override30days: isOverride30days,
  };

  await availability.appendDatesToShippingLevels(shippings.value, specificWareDataForDeliveryComputation);

  dialog.open('deliveryOptions', {
    options: shippings.value.sort((a, b) => a.priority - b.priority)
  });
}

const hasLimitedShipping = shopZone.value.allowedFeatures.includes('LIMITED_SHIPPING_INFO') && entity.shippingOptions.countries.isAnyDisabled;

const openLimitedShippingDialog = async () => {

  const allCountries = (await useApiFetch(`/api/limitedShippingDetails?idWares=${entity.id}&currencyId=${locale.getLocale().currencyId}&languageId=${locale.getLocale().languageId}`)).wares[0].shippingOptions.countries;

  dialog.open('limitedShipping', {
    countries: allCountries.checked.map((country) => {
      return {
        id: country.id,
        name: country.name,
        enabled: !allCountries.disabled.some(disabledCountry => disabledCountry.id === country.id)
      }
    })
  });
}

//#endregion

//#region watch dog

const watchDogOpened = ref(false);

const watchDogEmail = ref('');
const watchDogInput = ref(null);
const watchDogChecked = ref(false);

const addWatchDog = async () => {

  if (await watchDogInput.value.validate()) {

    if (watchDogChecked.value) {

      await useApiFetch(`/api/mutations/watchdog?email=${encodeURIComponent(watchDogEmail.value)}&variantId=${selectedVariant.value.id}&currencyId=${locale.getLocale().currencyId}&languageId=${locale.getLocale().languageId}`);

      watchDogEmail.value = '';
      watchDogInput.value.reset();

      message.show(i18n.t('E-mail byl zaznamenán, děkujeme'), null, false);
    }
    else {
      message.show(i18n.t('Pro odeslání formuláře je nutné souhlasit s obchodními podmínkami'), null, true);
    }
  }
  else {
    message.show(i18n.t('Vyplňte e-mail ve správném formátu'), null, true);
  }
}

//#endregion

//#region structured data & og tags

let structuredDataObject = {
  "@context": "https://schema.org/",
  "@type": "Product",
  "name": entity.name,
  "description": usePage().value.page.description
};

if (getParameterValue(appConfig.parameterIds.vendor)) {

  structuredDataObject.brand = {
    "@type": "Brand",
    "name": getParameterValue(appConfig.parameterIds.vendor)
  };
}

if (mainImageId.value) {
  structuredDataObject.image = [
    utils.getWareImageUrl(mainImageId.value, entity.name, "or")
  ];
}

if (entity.variants?.some(variant => variant.eanCode?.length)) {
  structuredDataObject.gtin13 = entity.variants.find(variant => variant.eanCode.length).eanCode[0].code;
}

if (entity.variants?.length) {

  let availabilitySchema = 'https://schema.org/OutOfStock';

  if (isElectronicDistribution) {
    availabilitySchema = 'https://schema.org/OnlineOnly';
  }
  else if (entity.variants.some(variant => variant.preorder) && entity.variants.every(variant => !variant.stored || variant.preorder)) {
    availabilitySchema = 'https://schema.org/PreOrder';
  }
  else if (entity.variants.some(variant => variant.stored)) {
    availabilitySchema = 'https://schema.org/InStock';
  }

  structuredDataObject.offers = {
    "@type": "Offer",
    "price": entity.price.minPrice.price,
    "priceCurrency": "CZK",
    "priceSpecification": {
      "@type": "UnitPriceSpecification",
      "price": entity.price.minPrice.price,
      "priceCurrency": locale.getLocale().currency.sign
    },
    "availability": availabilitySchema,
    "shippingDetails": {
      "@type": "OfferShippingDetails",
      "shippingRate": {
        "@type": "MonetaryAmount",
        "value": shippingPrice.value ?? 0,
        "currency": locale.getLocale().currency.sign
      }
    },
    "url": `https://${shopZone.value.domain}${useRoute().fullPath}`
  };
}

useHead({
  script: [{
    type: 'application/ld+json',
    innerHTML: JSON.stringify(structuredDataObject)
  }],
  __dangerouslyDisableSanitizers: ['script'],
});

if (mainImageId.value) {
  useSeoMeta({
    ogImage: utils.getWareImageUrl(mainImageId.value, entity.name, "or"),
    ogTitle: entity.name,
    ogType: 'product',
    ogUrl: `https://${shopZone.value.domain}${useRoute().fullPath}`,
  });
}

//#endregion

// Update the route when selector model changes
watch(() => selectorModel.value, (newModel, oldModel) => {

  if (mobileImages.value) {
    mobileImages.value.slideTo(0);
    mobileImages.value.updateSlidesData();
    mobileImages.value.updateSlideWidth();
  }

  if (newModel.selectedVariant?.id != oldModel.selectedVariant?.id) {

    if (entity.variants.length > 1) {
      if (newModel.selectedVariant) {
        router.replace({ hash: '#' + newModel.selectedVariant.id.toString() });

        if (process.client) {
          wareAnalytics.getItems([entity.id], [newModel.selectedVariant.id]).then(data => {

            analytics.pushEvent('view_item', {
              ecommerce: {
                items: data
              }
            });

          });
        }
      }
      else {
        router.replace({ hash: '#' });
      }
    }
    else if (process.client) {
      wareAnalytics.getItems([entity.id], [entity.variants[0].id]).then(data => {

        analytics.pushEvent('view_item', {
          ecommerce: {
            items: data
          }
        });

      });
    }

    watchDogOpened.value = false;
  }
}, { deep: true });

const conversionBarOpened = ref(false);
const loadedLexiconTerms = ref({});

onMounted(() => {

  if (process.client) {

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    wareAnalytics.getItems([entity.id]).then(data => {

      analytics.pushEvent('view_item', {
        ecommerce: {
          items: data
        }
      });

      if (services.length) {
        analytics.pushEvent('user_interaction', {
          interaction_name: 'engravable_item_view',
          engraving_item_name: entity.name,
          engraving_item_category: data?.[0]?.item_category
        });
      }
    });

    tippy('.lexicon-term', {
      arrow: true,
      animation: 'scale',
      allowHTML: true,
      onShow: async (instance) => {

        const pageId = instance.reference.dataset.idpage;

        if (!(pageId in loadedLexiconTerms.value)) {

          const data = await useApiFetch(`/api/lexiconTerm?pageId=${pageId}&languageId=${locale.getLocale().languageId}&currencyId=${locale.getLocale().currencyId}`);

          loadedLexiconTerms.value[pageId] = data.pageById[0];
        }

        const lexiconTermData = loadedLexiconTerms.value[pageId];

        if (lexiconTermData?.title && lexiconTermData?.entity?.annotation) {
          instance.setContent(`<div class="mb-4 text-xl font-bold">${lexiconTermData.title}</div>${lexiconTermData.entity.annotation}`);
        }
      }
    });
  }
});

onUnmounted(() => {
  if (process.client) {
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('resize', handleScroll);
  }
});

const handleScroll = () => {
  const boundary = useElementBounding(sectionsWrapper).top.value;
  conversionBarOpened.value = boundary < 0;
}

</script>

<style lang="postcss">
.page-ware {

  &__sub-header {
    @apply my-8 text-2xl uppercase text-primary-500;
  }

  &__additional-images {
    @apply flex gap-2 items-center mt-2;

    .ware-image--360::after {
      background-image: url('/img/360small.png');
    }
  }

  &__variant-selector {

    .variant-selector {

      &__groups__items,
      &__values__items {
        @apply gap-2 tablet:gap-3;
      }

      &__groups__items {
        @apply grid;
        grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
      }

      &__values__items {
        @apply mt-4;
      }

      &__group {
        @apply flex flex-col items-center;

        img {
          @apply p-1 flex-1;
        }
      }

      &__values {
        @apply mt-4;
      }

      &__value {
        @apply min-w-[30px] m-0 px-3 py-2 text-sm;
      }
    }
  }

  &__price {
    @apply flex flex-wrap items-center gap-4 text-4xl tracking-tight;

    &>span {
      @apply text-2xl text-neutral-400;
    }
  }

  &__description {

    h2,
    h3 {
      @apply text-lg font-bold text-primary-500 tracking-normal;
    }

    p {
      @apply my-4 text-lg text-black text-justify;
    }

    a {
      @apply text-secondary-500 underline;
    }

    table {
      @apply my-8 p-8 whitespace-nowrap !w-full !border-0 border-collapse;

      tr:nth-of-type(2n+1) {
        @apply bg-neutral-100;

        &>th:first-child, &>td:first-child {
          @apply bg-neutral-100;
        }
      }

      tr:nth-of-type(2n) {
        @apply bg-neutral-50;

        &>th:first-child, &>td:first-child {
          @apply bg-neutral-50;
        }
      }

      tr>th:first-child,
      tr>td:first-child {
        position: sticky;
        left: 0;
      }

      td {
        padding: 18px;
        text-align: right;
        border: 0px !important;

        p {
          @apply m-0;
        }
      }

      td:nth-of-type(1) {
        font-weight: bold;
        text-transform: uppercase;
        text-align: left;
        vertical-align: top;
      }
    }

    ul {
      @apply my-4 list-disc ml-5 marker:text-primary-500;
    }

    .table-wrapper {
      @apply my-8 overflow-x-auto;

      table {
        @apply my-0;
      }
    }
  }

  &__properties {
    @apply w-full text-black;

    tr {
      @apply bg-neutral-100 even:bg-neutral-50;

      td:first-of-type {
        @apply uppercase;
      }
    }

    td {
      @apply p-4 align-top;
    }
  }

  &__labels {
    @apply flex flex-wrap gap-x-2 gap-y-4 list-none;

    a {
      @apply inline-block px-3 py-2 uppercase text-xs bg-neutral-100 hover:no-underline;
    }
  }

  &__section {

    @apply max-h-[0] overflow-hidden transition-all tablet:w-full tablet:order-[1];

    &--open {
      animation: slide-toggle 0.3s;
      animation-fill-mode: forwards;
    }

    &__wrapper {
      @apply flex mt-8 gap-4 border border-neutral-100;
    }

    &__header {
      @apply flex justify-between items-center gap-4 px-6 py-4 border-b border-neutral-100 font-bold bg-neutral-500 text-neutral-50 cursor-pointer transition-all ease-in-out duration-300 tablet:border-b-0 tablet:text-center tablet:justify-center tablet:flex-1 tablet:order-[0] desktop:hidden desktop:border-b;

      .icon {
        @apply inline-block text-white rotate-90 transition-transform duration-300 ease-in-out tablet:hidden;
      }

      &--open {

        @apply tablet:bg-neutral-50 tablet:text-neutral-500;

        .icon {
          @apply -rotate-90;
        }
      }
    }

    &__menu {

      @apply hidden relative w-[250px] desktop:block;

      .page-ware__section__header {
        @apply flex text-left justify-start;
      }
    }

    &__label {
      @apply hidden mb-4 text-lg font-bold text-primary-500 tracking-normal desktop:block;
    }

    &__inner {
      @apply p-4;
    }
  }

  &__alternative-link {
    @apply flex items-start tablet:block desktop:pr-6 desktop:border-r desktop:border-neutral-300;

    &:last-of-type {
      @apply desktop:mr-0 desktop:pr-0 desktop:border-r-0;
    }
  }

  .lexicon-term {
    @apply no-underline border-b border-dashed border-neutral-500;
  }

  &__description .lexicon-term {
    @apply whitespace-nowrap;
  }
}
</style>